// stores/apparelStore.ts
import { defineStore } from 'pinia'

export type BasketItem = {
    variantId: number
    qty: number
    label: string
    unitPrice: number
}

type StoredBasket = {
    version: 1
    basket: BasketItem[]
}

const STORAGE_KEY = 'thebunker_apparel_basket_v1'

const clampInt = (n: unknown, min = 0, max = 1_000_000) => {
    const x = typeof n === 'number' ? n : parseInt(String(n ?? ''), 10)
    if (!Number.isFinite(x)) return min
    return Math.max(min, Math.min(max, Math.floor(x)))
}

const clampMoney = (n: unknown, min = 0, max = 1_000_000) => {
    const x = typeof n === 'number' ? n : parseFloat(String(n ?? ''))
    if (!Number.isFinite(x)) return min
    return Math.max(min, Math.min(max, x))
}

export const useApparelStore = defineStore('apparel', {
    state: () => ({
        basket: [] as BasketItem[],
        hydrated: false as boolean,
    }),

    getters: {
        basketCount: (s) => s.basket.reduce((a, b) => a + clampInt(b.qty, 0, 10_000), 0),
        subtotal: (s) => s.basket.reduce((a, b) => a + clampMoney(b.unitPrice) * clampInt(b.qty, 0, 10_000), 0),
        isEmpty: (s) => s.basket.length === 0,
    },

    actions: {
        hydrate() {
            if (!process.client) return
            if (this.hydrated) return

            try {
                const raw = localStorage.getItem(STORAGE_KEY)
                if (!raw) {
                    this.hydrated = true
                    return
                }

                const parsed = JSON.parse(raw) as StoredBasket
                if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.basket)) {
                    localStorage.removeItem(STORAGE_KEY)
                    this.hydrated = true
                    return
                }

                const cleaned: BasketItem[] = parsed.basket
                    .map((x: any) => ({
                        variantId: clampInt(x?.variantId, 0),
                        qty: clampInt(x?.qty, 0, 10_000),
                        label: typeof x?.label === 'string' ? x.label.slice(0, 200) : '',
                        unitPrice: clampMoney(x?.unitPrice, 0, 1_000_000),
                    }))
                    .filter((x) => x.variantId > 0 && x.qty > 0 && x.label)

                this.basket = cleaned
            } catch {
                // If storage is corrupted, reset it
                try {
                    localStorage.removeItem(STORAGE_KEY)
                } catch {
                    /* ignore */
                }
            } finally {
                this.hydrated = true
            }
        },

        persist() {
            if (!process.client) return
            try {
                const payload: StoredBasket = { version: 1, basket: this.basket }
                localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
            } catch {
                /* ignore */
            }
        },

        ensureHydrated() {
            if (!this.hydrated) this.hydrate()
        },

        add(variantId: number, label: string, unitPrice: number, qty = 1) {
            this.ensureHydrated()

            const vid = clampInt(variantId, 0)
            const q = clampInt(qty, 1, 10_000)
            const price = clampMoney(unitPrice, 0, 1_000_000)
            const safeLabel = String(label || '').trim().slice(0, 200)

            if (!vid || !q || !safeLabel) return

            const existing = this.basket.find((b) => b.variantId === vid)
            if (existing) {
                existing.qty = clampInt(existing.qty + q, 1, 10_000)
                // Keep label/price updated (helps if you change pricing later)
                existing.label = safeLabel
                existing.unitPrice = price
            } else {
                this.basket.push({ variantId: vid, qty: q, label: safeLabel, unitPrice: price })
            }

            this.persist()
        },

        setQty(variantId: number, qty: number) {
            this.ensureHydrated()

            const vid = clampInt(variantId, 0)
            const q = clampInt(qty, 0, 10_000)

            if (!vid) return

            if (q <= 0) {
                this.basket = this.basket.filter((b) => b.variantId !== vid)
            } else {
                const existing = this.basket.find((b) => b.variantId === vid)
                if (existing) existing.qty = q
            }

            this.persist()
        },

        increment(variantId: number, by = 1) {
            this.ensureHydrated()
            const vid = clampInt(variantId, 0)
            const delta = clampInt(by, 1, 10_000)
            if (!vid) return

            const existing = this.basket.find((b) => b.variantId === vid)
            if (existing) existing.qty = clampInt(existing.qty + delta, 1, 10_000)

            this.persist()
        },

        remove(variantId: number) {
            this.ensureHydrated()
            const vid = clampInt(variantId, 0)
            if (!vid) return

            this.basket = this.basket.filter((b) => b.variantId !== vid)
            this.persist()
        },

        clear() {
            this.ensureHydrated()
            this.basket = []
            this.persist()
        },

        toOrderItems() {
            this.ensureHydrated()
            return this.basket.map((b: BasketItem) => ({
                variantId: b.variantId,
                qty: b.qty,
            }))
        },
    },
})
